/**
 * Shared Combat Logic
 * 
 * This module contains pure functions for combat calculations.
 * Used by both client and server to ensure consistent gameplay.
 * 
 * @module shared/logic/CombatLogic
 */

import { conditionalAbilityDamageMultiplier } from './ConditionalAbilityDamage';
import { DERIVED_STAT_COEFFICIENTS as K, isDexScalingClass, isIntScalingClass } from './derivedStatCoefficients';
import { abilityBaseDamage, isMagicalAbility } from './abilityBaseDamage';

// Moved to ./abilityBaseDamage (plan 29 F2): the base number the tooltip shows.
export { abilityBaseDamage, abilityAttackStat, isMagicalAbility, calculateBaseDamage } from './abilityBaseDamage';
import { calculateHealthMana, getClassConfig } from '../classes/ClassUtils';
import type { CharacterClass } from '../CharacterTypes';

export interface CombatStats {
  /** Live target HP, supplied by the authoritative owner for execute bonuses. */
  health?: number;
  maxHealth?: number;
  level: number;
  heroClass?: string;
  criticalRate?: number;
  attack?: number;
  magicAttack?: number;
  /** Equip-derived physical mitigation (read by calculateDamage; 2026-06-27). */
  defense?: number;
  /** Equip-derived magical mitigation (used for magical-school attacks). */
  magicDefense?: number;
  /**
   * Crit multiplier on a critical hit. Derived (class base + gear/set bonus)
   * by recalculatePlayerStats. calculateDamage uses it with the class base as
   * a hard floor, so it can only raise crits above class baseline, never below
   * (covers un-derived entities — bots/NPCs/fixtures — safely; 2026-06-28).
   */
  criticalDamage?: number;
}

export interface CombatResult {
  damage: number;
  critical: boolean;
  school?: DamageSchool;
  schoolEffectiveness?: 'normal' | 'strong' | 'weak';
  blocked?: boolean;
  dodged?: boolean;
  resisted?: boolean;
}

export type DamageSchool = 'physical' | 'fire' | 'frost' | 'arcane' | 'shadow' | 'holy' | 'nature' | 'chaos' | 'healing';

export interface AttackParams {
  abilityId?: string;
  isBasicAttack?: boolean;
  school?: DamageSchool;
  seed?: number; // For deterministic random generation
}

// Class advantage matrix (rock-paper-scissors)
// Warrior > Mage > Hunter > Paladin > Warrior
const ADVANTAGE_MATRIX: Record<string, Record<string, number>> = {
  'warrior': { 'mage': 1.5, 'hunter': 0.8, 'paladin': 0.9, 'default': 1.0 },
  'mage': { 'hunter': 1.5, 'paladin': 0.8, 'warrior': 0.9, 'default': 1.0 },
  'hunter': { 'paladin': 1.5, 'warrior': 0.8, 'mage': 0.9, 'default': 1.0 },
  'paladin': { 'warrior': 1.5, 'mage': 0.8, 'hunter': 0.9, 'default': 1.0 }
};

// Class critical rate bonuses
const CLASS_CRITICAL_BONUS: Record<string, number> = {
  'hunter': 0.05,   // Hunters have higher critical rate
  'warrior': 0.02,
  'mage': 0.01,
  'paladin': 0.01
};

// Class critical multipliers
const CLASS_CRITICAL_MULTIPLIER: Record<string, number> = {
  'warrior': 2.2,   // Warriors hit harder on crits
  'hunter': 2.0,
  'mage': 1.8,      // Mages have lower crit multiplier but more frequent spells
  'paladin': 2.1
};

// ── Element Effectiveness ──────────────────────────────────────────
// Schools that deal bonus damage to other schools (1.25x)
// Physical is neutral (1.0x to everything)
const ELEMENT_STRONG_AGAINST: Partial<Record<DamageSchool, DamageSchool[]>> = {
  fire:   ['frost', 'nature'],
  frost:  ['nature', 'fire'],   // frost slows fire but fire melts frost — mutual
  arcane: ['shadow'],
  shadow: ['holy'],
  holy:   ['shadow'],
  nature: ['arcane'],
};

// Schools that deal reduced damage to other schools (0.75x)
const ELEMENT_WEAK_AGAINST: Partial<Record<DamageSchool, DamageSchool[]>> = {
  fire:   ['fire'],      // Fire resists fire
  frost:  ['frost'],
  arcane: ['arcane'],
  shadow: ['shadow'],
  holy:   ['holy'],
  nature: ['nature'],
};

const ELEMENT_STRONG_MULT = 1.25;
const ELEMENT_WEAK_MULT = 0.75;

// Defender mitigation curve. Diminishing returns so high defense never zeroes
// damage: finalDmg *= K / (K + effectiveDefense). At def=K, damage is halved;
// def=2K → one third. K=100 keeps early-game armor meaningful but not absolute.
const DEFENSE_MITIGATION_K = 100;

/**
 * Get element effectiveness multiplier.
 * Physical and chaos are neutral (1.0x). Healing bypasses this.
 */
export function getSchoolMultiplier(
  attackSchool?: DamageSchool,
  defenderSchool?: DamageSchool
): number {
  if (!attackSchool || attackSchool === 'physical' || attackSchool === 'chaos' || attackSchool === 'healing') return 1.0;
  if (!defenderSchool) return 1.0;

  if (ELEMENT_STRONG_AGAINST[attackSchool]?.includes(defenderSchool)) return ELEMENT_STRONG_MULT;
  if (ELEMENT_WEAK_AGAINST[attackSchool]?.includes(defenderSchool)) return ELEMENT_WEAK_MULT;
  return 1.0;
}

/**
 * Whether a school uses magic attack (true) or physical attack (false).
 */
export function isMagicSchool(school?: DamageSchool): boolean {
  if (!school) return false;
  return school !== 'physical';
}

// ── Combat Tuning Constants ────────────────────────────────────────
// Centralised here so balance changes only require touching one block.

/** Level multiplier: bonus per level advantage (capped at MAX_LEVEL_BONUS) */
const LEVEL_ADVANTAGE_PER_LEVEL = 0.05;
const MAX_LEVEL_BONUS = 0.5;
/** Level multiplier: penalty per level disadvantage (capped at MAX_LEVEL_PENALTY) */
const LEVEL_DISADVANTAGE_PER_LEVEL = 0.02;
const MAX_LEVEL_PENALTY = -0.3;

/** Base critical rate: base% + (level * per-level%) */
const BASE_CRIT_RATE = 0.05;
const CRIT_RATE_PER_LEVEL = 0.005;

/** Damage variance: ± this fraction (0.2 = ±20%) */
const DAMAGE_VARIANCE_RANGE = 0.4;
const DAMAGE_VARIANCE_OFFSET = 0.2;

/** XP reward: bonus per enemy level above player */
const XP_BONUS_PER_LEVEL = 0.2;
/** XP reward: penalty per level below player (kicks in after 5 levels) */
const XP_PENALTY_PER_LEVEL = 0.1;
const XP_PENALTY_THRESHOLD = -5;
const XP_MIN_MULTIPLIER = 0.1;

/**
 * Deterministic random number generator for seeded combat
 * Ensures client and server get same results with same seed
 */
export function deterministicRandom(seed: number): number {
  // Simple xorshift for deterministic results
  let x = seed;
  x ^= x << 13;
  x ^= x >> 17;
  x ^= x << 5;
  return (x >>> 0) / 4294967296; // Normalize to 0-1
}

/**
 * Get class advantage multiplier
 */
export function getClassMultiplier(
  attackerClass: string,
  defenderClass: string
): number {
  const matrix = ADVANTAGE_MATRIX[attackerClass];
  if (!matrix) return 1.0;
  return matrix[defenderClass] || matrix['default'] || 1.0;
}

/**
 * Get level difference multiplier
 * +5% per level advantage (max +50%)
 * -2% per level disadvantage (max -30%)
 */
export function getLevelMultiplier(
  attackerLevel: number,
  defenderLevel: number
): number {
  const levelDiff = attackerLevel - defenderLevel;

  if (levelDiff > 0) {
    const bonus = Math.min(levelDiff * LEVEL_ADVANTAGE_PER_LEVEL, MAX_LEVEL_BONUS);
    return 1.0 + bonus;
  } else if (levelDiff < 0) {
    const penalty = Math.max(levelDiff * LEVEL_DISADVANTAGE_PER_LEVEL, MAX_LEVEL_PENALTY);
    return 1.0 + penalty;
  }

  return 1.0;
}

/**
 * Get critical hit rate
 */
export function getCriticalRate(
  attackerLevel: number,
  attackerClass: string,
  attackerCriticalRate?: number
): number {
  // Use provided critical rate if available
  if (attackerCriticalRate !== undefined) {
    return attackerCriticalRate > 1
      ? attackerCriticalRate / 100  // Convert from percentage
      : attackerCriticalRate;        // Already 0-1
  }

  const baseRate = BASE_CRIT_RATE + (attackerLevel * CRIT_RATE_PER_LEVEL);
  const classBonus = CLASS_CRITICAL_BONUS[attackerClass] || 0;

  return baseRate + classBonus;
}

/**
 * Roll for critical hit
 */
export function rollCritical(
  criticalRate: number,
  seed?: number
): boolean {
  const random = seed !== undefined
    ? deterministicRandom(seed)
    : Math.random();
  return random < criticalRate;
}

/**
 * Get critical damage multiplier
 */
export function getCriticalMultiplier(attackerClass: string): number {
  return CLASS_CRITICAL_MULTIPLIER[attackerClass] || 2.0;
}

/**
 * Get random variance for damage (±20%)
 */
export function getRandomVariance(seed?: number): number {
  const random = seed !== undefined
    ? deterministicRandom(seed + 1)
    : Math.random();
  return (random * DAMAGE_VARIANCE_RANGE) - DAMAGE_VARIANCE_OFFSET;
}

/** The ±fraction of random variance `calculateDamage` applies. */
export const DAMAGE_VARIANCE_FRACTION = DAMAGE_VARIANCE_OFFSET;

/**
 * Main damage calculation function
 * This is the SINGLE SOURCE OF TRUTH for combat damage
 */
export function calculateDamage(
  attacker: CombatStats,
  defender: CombatStats,
  params: AttackParams = {}
): CombatResult {
  // Get base damage — pass attacker's relevant stat for TOML-driven scaling
  const isMagical = isMagicalAbility(params.abilityId);
  const baseDamage = abilityBaseDamage(attacker, params.abilityId);

  // Apply class advantage
  const classMultiplier = getClassMultiplier(
    attacker.heroClass || 'warrior',
    defender.heroClass || 'warrior'
  );

  // Apply level difference
  const levelMultiplier = getLevelMultiplier(
    attacker.level,
    defender.level
  );

  // Calculate critical
  const criticalRate = getCriticalRate(
    attacker.level,
    attacker.heroClass || 'warrior',
    attacker.criticalRate
  );
  const isCritical = rollCritical(criticalRate, params.seed);
  // Class base is the floor; a derived criticalDamage (class base + gear/set
  // bonus) can raise it but never lower it — so un-derived attackers (bots,
  // fixtures, the stale 1.5 schema default) keep their full class crit.
  const criticalMultiplier = isCritical
    ? Math.max(attacker.criticalDamage ?? 0, getCriticalMultiplier(attacker.heroClass || 'warrior'))
    : 1.0;

  // Apply element effectiveness (school vs school)
  const schoolMultiplier = getSchoolMultiplier(params.school, defender.heroClass as DamageSchool | undefined);

  // Calculate final damage
  let damage = baseDamage * classMultiplier * levelMultiplier * criticalMultiplier * schoolMultiplier
    * conditionalAbilityDamageMultiplier(params.abilityId, defender);

  // Apply variance
  const variance = getRandomVariance(params.seed);
  damage = damage * (1 + variance);

  // Apply defender mitigation (equip-derived defense). Previously UNWIRED —
  // CombatStats had no defense field, so armor was cosmetic (2026-06-27 fix).
  // Magical-school attacks are mitigated by magicDefense, physical by defense.
  const effectiveDefense = isMagical
    ? (defender.magicDefense ?? defender.defense ?? 0)
    : (defender.defense ?? 0);
  if (effectiveDefense > 0) {
    damage = damage * (DEFENSE_MITIGATION_K / (DEFENSE_MITIGATION_K + effectiveDefense));
  }

  // Round and ensure minimum
  damage = Math.round(damage);
  damage = Math.max(1, damage);

  return {
    damage,
    critical: isCritical,
    school: params.school,
    schoolEffectiveness: schoolMultiplier > 1 ? 'strong' : schoolMultiplier < 1 ? 'weak' : 'normal',
  };
}

/**
 * Calculate XP reward for defeating an enemy
 */
export function calculateXPReward(
  playerLevel: number,
  enemyLevel: number,
  baseXP: number = 10
): number {
  const levelDiff = enemyLevel - playerLevel;

  // Base XP modified by level difference
  let multiplier = 1.0;

  if (levelDiff > 0) {
    multiplier = 1.0 + (levelDiff * XP_BONUS_PER_LEVEL);
  } else if (levelDiff < XP_PENALTY_THRESHOLD) {
    multiplier = Math.max(XP_MIN_MULTIPLIER, 1.0 + (levelDiff * XP_PENALTY_PER_LEVEL));
  }

  return Math.round(baseXP * multiplier);
}

/**
 * Calculate stat points gained on level up
 */
export function getStatPointsOnLevelUp(level: number): number {
  // More points at lower levels, tapering off at higher levels
  if (level <= 10) return 5;
  if (level <= 20) return 4;
  if (level <= 30) return 3;
  return 2;
}

/**
 * Calculate max HP from STR (Strength)
 */
export function calculateMaxHP(strength: number, level: number): number {
  return 100 + (strength * 5) + (level * 10);
}

/**
 * Calculate max MP from INT (Intelligence)
 */
export function calculateMaxMP(intelligence: number, level: number): number {
  return 50 + (intelligence * 5) + (level * 5);
}

/**
 * Calculate attack power from STR + equipment
 */
export function calculateAttack(strength: number, equipmentBonus: number = 0): number {
  return (strength * 2) + equipmentBonus;
}

/**
 * Calculate magic attack from INT + equipment
 */
export function calculateMagicAttack(
  intelligence: number,
  equipmentBonus: number = 0
): number {
  return (intelligence * 2) + equipmentBonus;
}


/**
 * Calculate derived stats from primary stats
 * STR -> ATK (and legacy classless-fixture HP only)
 * INT -> MATK (and legacy classless-fixture MP only)
 * DEX -> ACC, AVO, DEF
 * LUK -> CRIT, MDEF
 */
export function calculateDerivedStats(
  stats: {
    strength: number;
    dexterity: number;
    intelligence: number;
    luck: number;
    level: number;
    health?: number;
    mana?: number;
    /**
     * Character class. When provided, maxHealth/maxMana are derived from the
     * canonical class-multiplier formula (`calculateHealthMana`) — the SAME
     * source of truth the client (`createHelix2000Character`), the
     * persisted-character creation route, and `Player.setupFromCharacterData`
     * all use. Without it, this falls back to the legacy class-agnostic
     * STR/INT formula (kept only for callers that don't know the class — e.g.
     * fixtures/tests). Passing the class is what keeps the Colyseus schema's
     * maxHealth equal to the local player model's maxHealth (was 160 vs 80 for
     * mage when this used the STR-only formula).
     */
    characterClass?: string;
  }
): {
  maxHealth: number;
  maxMana: number;
  attack: number;
  magicAttack: number;
  defense: number;
  magicDefense: number;
  accuracy: number;
  avoidability: number;
  criticalRate: number;
  speed: number;
  jump: number;
  health: number;
  mana: number;
} {
  // maxHealth/maxMana — canonical when the class is known (class-multiplier
  // formula, identical to `calculateHealthMana`), else the legacy STR/INT
  // fallback. Keeping ONE formula here is what unifies the server schema with
  // the local player model.
  let maxHealth: number;
  let maxMana: number;
  if (stats.characterClass && getClassConfig(stats.characterClass as CharacterClass)) {
    const hpm = calculateHealthMana(stats.characterClass as CharacterClass, stats.level);
    maxHealth = hpm.maxHealth;
    maxMana = hpm.maxMana;
  } else {
    // Legacy fallback (class unknown): 1 STR = 5 HP + (level*10); 1 INT = 5 MP + (level*5)
    maxHealth = 100 + (stats.strength * 5) + (stats.level * 10);
    maxMana = 50 + (stats.intelligence * 5) + (stats.level * 5);
  }

  // Coefficients live in derivedStatCoefficients.ts; the character sheet prints the same numbers.
  const isDexClass = isDexScalingClass(stats.characterClass);
  const attack = isDexClass
    ? Math.floor(stats.strength * K.attack.dexClassStr + stats.dexterity * K.attack.dexClassDex)
    : Math.floor(stats.strength * (stats.characterClass ? K.attack.str : K.attack.classlessStr));
  const magicAttack = Math.floor(stats.intelligence * (isIntScalingClass(stats.characterClass) ? K.magicAttack.casterInt : K.magicAttack.otherInt));
  const defense = Math.floor(stats.dexterity * K.defense.dex);
  const magicDefense = Math.floor(stats.luck * K.magicDefense.luk);
  const accuracy = Math.floor(stats.dexterity * K.accuracy.dex + stats.luck * K.accuracy.luk);
  const avoidability = Math.floor(stats.luck * K.avoidability.luk + stats.dexterity * K.avoidability.dex);
  const criticalRate = K.criticalRate.base
    + Math.floor(stats.luck * K.criticalRate.luk + (isDexClass ? stats.dexterity * K.criticalRate.dexClassDex : 0));
  const speed = K.speed.base + Math.floor(stats.dexterity * K.speed.dex);
  const jump = K.jump.base + Math.floor(stats.strength * K.jump.str + stats.dexterity * K.jump.dex);

  // Clamp current HP/MP if provided, otherwise default to full
  const currentHealth = stats.health !== undefined
    ? Math.min(stats.health, maxHealth)
    : maxHealth;

  const currentMana = stats.mana !== undefined
    ? Math.min(stats.mana, maxMana)
    : maxMana;

  return {
    maxHealth,
    maxMana,
    attack,
    magicAttack,
    defense,
    magicDefense,
    accuracy,
    avoidability,
    criticalRate,
    speed,
    jump,
    health: currentHealth, // Return clamped values
    mana: currentMana
  };
}
